import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import './PaymentForm.css';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../context/AuthContext';
import { subscriptionsApi } from '../../api/subscriptions';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Development mode flag
const isDevelopment = process.env.NODE_ENV === 'development';

const CheckoutForm = ({
	amount,
	planName,
	planId,
	organizationId,
	onSuccess,
	onError,
}) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const { orgData } = useAuth();
	const primaryColor = orgData?.primaryColor || '#4b95a3';

	// Development mode state
	const [devCardNumber] = useState('4242 4242 4242 4242');
	const [devExpiry] = useState('12/25');
	const [devCvc] = useState('123');

	const handleSubmit = async (event) => {
		event.preventDefault();
		setLoading(true);
		setError(null);

		try {
			// For development mode, we'll simulate a successful payment
			if (isDevelopment) {
				// Even in development, we should try to create a checkout session
				// This helps test the API integration without processing real payments
				const data = {
					priceId: planId,
					planType: planName,
					features: [],
					maxUsers: null,
				};

				try {
					const response =
						await subscriptionsApi.createOrganizationSubscription(
							organizationId,
							data
						);

					// If we get a URL back, redirect to it
					if (response.url) {
						window.location.href = response.url;
						return;
					}
				} catch (devErr) {
					console.log(
						'Dev mode API call failed, using fallback:',
						devErr
					);
					// If the API call fails in dev mode, fall back to the simulation
				}

				// Fallback for development if the API call fails
				setTimeout(() => {
					if (onSuccess) {
						onSuccess({
							id: 'dev_payment_' + Date.now(),
							status: 'succeeded',
							amount: amount * 100,
						});
					}
					setLoading(false);
				}, 1500);
				return;
			}

			// Production mode code
			const data = {
				priceId: planId,
				planType: planName,
				features: [],
				maxUsers: null,
			};

			const response =
				await subscriptionsApi.createOrganizationSubscription(
					organizationId,
					data
				);

			// Redirect to Stripe Checkout
			if (response.url) {
				window.location.href = response.url;
			} else {
				setError('Failed to create checkout session: No URL returned');
			}
		} catch (err) {
			console.error('Subscription creation error:', err);
			setError(err.message || 'An error occurred during checkout');
			if (onError) onError(err);
		} finally {
			if (!isDevelopment) {
				setLoading(false);
			}
		}
	};

	const buttonStyle = {
		backgroundColor: primaryColor,
	};

	return (
		<form
			onSubmit={handleSubmit}
			className='payment-form'>
			<div className='form-group'>
				<label className='form-label'>Card Details</label>

				{isDevelopment ? (
					// Development mode inputs
					<div className='dev-card-inputs'>
						<div className='dev-input-group'>
							<label>Card Number</label>
							<input
								type='text'
								value={devCardNumber}
								readOnly
								placeholder='4242 4242 4242 4242'
								className='dev-card-input'
							/>
						</div>
						<div className='dev-input-row'>
							<div className='dev-input-group'>
								<label>Expiry</label>
								<input
									type='text'
									value={devExpiry}
									readOnly
									placeholder='MM/YY'
									className='dev-card-input'
								/>
							</div>
							<div className='dev-input-group'>
								<label>CVC</label>
								<input
									type='text'
									value={devCvc}
									readOnly
									placeholder='123'
									className='dev-card-input'
								/>
							</div>
						</div>
						<p className='dev-mode-notice'>
							Development Mode - No actual payment will be
							processed
						</p>
					</div>
				) : (
					// For production, we'll just show a message since we're redirecting to Stripe Checkout
					<div className='checkout-redirect-message'>
						<p>
							You'll be redirected to Stripe's secure checkout
							page to complete your payment.
						</p>
					</div>
				)}
			</div>

			{error && <div className='error-message'>{error}</div>}

			<button
				type='submit'
				disabled={loading}
				className='payment-button'
				style={buttonStyle}>
				{loading
					? 'Processing...'
					: `Proceed to Payment - $${amount.toFixed(2)}`}
			</button>
		</form>
	);
};

const PaymentForm = ({
	amount = 19.99,
	planName = 'Monthly Subscription',
	planId,
	onPaymentSuccess,
}) => {
	const { isDarkMode } = useTheme();
	const { orgData } = useAuth();
	const primaryColor = orgData?.primaryColor || '#4b95a3';

	return (
		<div
			className='payment-container'
			data-theme={isDarkMode ? 'dark' : 'light'}>
			<h2 style={{ color: primaryColor }}>Complete Your Subscription</h2>
			<div className='plan-details'>
				<h3>{planName}</h3>
				<p className='plan-price'>${amount.toFixed(2)}</p>
			</div>

			<Elements stripe={stripePromise}>
				<CheckoutForm
					amount={amount}
					planName={planName}
					planId={planId}
					organizationId={orgData?.id}
					onSuccess={(paymentIntent) => {
						if (onPaymentSuccess) onPaymentSuccess(paymentIntent);
					}}
					onError={(error) => {
						console.error('Payment failed:', error);
					}}
				/>
			</Elements>
		</div>
	);
};

export default PaymentForm;
